import { toast, errorToast } from "@/v2-ui/toast"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import tenantStub from "@/v2-backoffice-client/tenant/tenant.stub"
import { useTenantCreateMutation } from "@/v2-backoffice-client/tenant/create/tenant.create.mutation"
import { useCustomerCreateMutation } from "@/v2-backoffice-client/customer/create/customer.create.mutation"
import type { InvitationSection } from "@/v2-backoffice-client/invitation/create/invitation.create.sections"

/**
 * The send-time auto-create flow for unmatched imported sections (FUS-927
 * CSM ask): the customer first when none matched by name (teams attach to
 * customers through the external id — seeded with a generated uuid, since
 * app-born customers have none until CRM-linked), then the team. This hook
 * owns both mutations so the form stays a composition; failures toast here
 * and surface as a null return.
 */
export function useInvitationTeamAutoCreate() {
  const [createTenant, { error: createTenantError }] = useTenantCreateMutation()
  errorToast(createTenantError, { title: "Failed to create the team" })
  const [createCustomer, { error: createCustomerError }] =
    useCustomerCreateMutation()
  errorToast(createCustomerError, { title: "Failed to create the customer" })

  return async function createTeamForSection(
    section: InvitationSection,
  ): Promise<TenantFragment | null> {
    if (!section.imported) return null
    let externalCustomerId =
      section.imported.suggestedCustomer?.ExternalCustomerId
    if (!externalCustomerId) {
      // No `crm` argument on purpose: a team-named placeholder customer must
      // never become a HubSpot company.
      const customerResult = await createCustomer({
        variables: {
          input: {
            Name: section.imported.teamName,
            ExternalCustomerId: crypto.randomUUID(),
          },
        },
      })
      const customer = customerResult.data?.createCustomer.Customer
      if (customerResult.errors?.length || !customer?.ExternalCustomerId) {
        if (!customerResult.errors?.length) {
          toast({
            variant: "destructive",
            title: `Couldn't prepare customer “${section.imported.teamName}”`,
            description: "The created customer came back without a join key.",
          })
        }
        return null
      }
      externalCustomerId = customer.ExternalCustomerId
    }
    const result = await createTenant({
      variables: {
        input: {
          Name: section.imported.teamName,
          ExternalCustomerId: externalCustomerId,
        },
      },
    })
    const created = result.data?.createTenant
    if (result.errors?.length || !created) return null
    // Stub first: it only backfills the fragment's stat fields — the
    // created row's real Id/Name must win.
    return { ...tenantStub, ...created }
  }
}
